import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FilterSidebar from './FilterSidebar';
import LocationCard from './LocationCard';
import Reveal from '../../components/Reveal';
import Seo from '../../components/Seo';
import { getLocations, categoryKey } from '../../content/catalog';
import { useLang } from '../../i18n/useLocalizedNav';

const Locations: React.FC = () => {
  const lang = useLang();
  const { t } = useTranslation();
  // Static localized catalog — rendered into the prerendered HTML, no
  // API call. The API is only used at runtime for auth + claiming.
  const locations = useMemo(() => getLocations(lang), [lang]);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters live in the URL so a filtered view is shareable.
  const activeCategory = searchParams.get('category');
  const activeRegion = searchParams.get('region');

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const regions = useMemo(
    () =>
      Array.from(
        new Set(locations.map((l) => l.region).filter(Boolean) as string[]),
      ).sort(),
    [locations],
  );

  // Categories are derived from the data (not hardcoded) and ordered by
  // count, descending — a new DB category appears here automatically.
  const { categories, categoryCounts } = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const l of locations) {
      counts[l.category] = (counts[l.category] ?? 0) + 1;
    }
    const ordered = Object.keys(counts).sort(
      (a, b) => counts[b] - counts[a] || a.localeCompare(b),
    );
    return { categories: ordered, categoryCounts: counts };
  }, [locations]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return locations.filter((l) => {
      if (activeCategory && l.category !== activeCategory) return false;
      if (activeRegion && l.region !== activeRegion) return false;
      if (!q) return true;
      // Front-end search across name, region, address, category, stamp.
      const haystack = [
        l.name,
        l.region,
        l.address,
        l.category,
        l.stampName,
        l.metaText,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [locations, activeCategory, activeRegion, search]);

  const contextLabel = [
    activeRegion && t('loc_in_region', { region: activeRegion }),
    activeCategory && t(categoryKey(activeCategory)),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-8">
      <Seo
        titleKey="seo.locations_title"
        descriptionKey="seo.locations_description"
      />
      <div className="space-y-6">
        <div>
          <p className="font-heading font-bold text-sm tracking-[0.25em] text-brand-accent uppercase">
            {t('loc_eyebrow')}
          </p>
          <h1 className="font-display text-warm-800 text-4xl md:text-5xl lg:text-6xl leading-[1.05] mt-3">
            {t('loc_title_1', { count: locations.length })}
            <br />
            <span className="italic">{t('loc_title_accent')}</span>
          </h1>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-500" />
          <input
            type="text"
            placeholder={t('loc_search_placeholder')}
            className="w-full pl-11 pr-4 py-3 bg-white border border-warm-200 rounded-xl focus:ring-2 focus:ring-brand-primary outline-none text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
        <FilterSidebar
          total={locations.length}
          categories={categories}
          categoryCounts={categoryCounts}
          regions={regions}
          activeCategory={activeCategory}
          activeRegion={activeRegion}
          onCategory={(c) => setParam('category', c)}
          onRegion={(r) => setParam('region', r)}
        />

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-warm-500 text-sm">
              {t('loc_showing')}{' '}
              <span className="font-bold text-warm-800">
                {filtered.length}
              </span>
              {contextLabel && ` ${contextLabel}`}
            </p>
            {(activeCategory || activeRegion || search) && (
              <button
                onClick={() => {
                  setSearchParams({}, { replace: true });
                  setSearch('');
                }}
                className="text-sm font-heading font-bold text-brand-accent hover:underline"
              >
                {t('loc_clear_filters')}
              </button>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-warm-200">
              <p className="text-warm-500">
                {t('loc_no_match')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((loc, i) => (
                <Reveal key={loc.id} delay={(i % 6) * 50} className="h-full">
                  <LocationCard location={loc} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Locations;
